import type { ReactNode } from "react";
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { GlassCard, Spinner, EmptyState, SeverityCapsule, Pill, Button } from "../../components/ui";
import { MediaBadge } from "../../components/MediaIndicator";
import { EMESRT_COLOR, fmtTime, timeAgo } from "../../lib/format";
import type { EventRow, Severity } from "../../types/api";

const STATUS_COLOR: Record<string, string> = {
  new: "#F59E0B", acknowledged: "#22D3EE", in_review: "#3B82F6",
  classified: "#2DD4BF", actioned: "#2DD4BF", closed: "#64748B",
};

interface Col { key: string; label: string; sort?: string; render: (r: EventRow) => ReactNode; width?: string; }

const COLUMNS: Col[] = [
  { key: "sev", label: "Severity", sort: "severity", render: (r) => <SeverityCapsule severity={r.severity as Severity} /> },
  { key: "id", label: "Case", render: (r) => <span className="mono text-slatey">#{r.ext_id}</span> },
  { key: "time", label: "Time", sort: "start_time", render: (r) => (
    <span title={fmtTime(r.start_time)}>{timeAgo(r.start_time)}</span>
  ) },
  { key: "type", label: "Type", render: (r) => (
    <span className="inline-flex items-center gap-1.5">
      {r.event_type_label ?? "—"}
      {r.emesrt_level && <Pill color={EMESRT_COLOR[r.emesrt_level] ?? "#64748B"}>{r.emesrt_level}</Pill>}
    </span>
  ) },
  { key: "risk", label: "Risk", sort: "risk_score", render: (r) => (
    <span className="mono">{r.risk_score != null ? r.risk_score.toFixed(0) : "—"}</span>
  ) },
  { key: "status", label: "Status", render: (r) => <Pill color={STATUS_COLOR[r.status] ?? "#64748B"}>{r.status.replace("_", " ")}</Pill> },
  { key: "site", label: "Site", render: (r) => r.site_title ?? "—" },
  { key: "asset", label: "Asset", render: (r) => (
    <span className="min-w-0"><span className="block truncate">{r.asset_registration ?? "—"}</span>
      {r.machine_type && <span className="block truncate text-[10px] text-slatey">{r.machine_type}</span>}</span>
  ) },
  { key: "media", label: "Media", render: (r) => (r.media ? <MediaBadge media={r.media} /> : "—") },
  { key: "channel", label: "Ch", render: (r) => <span className="mono text-slatey">{r.channel ?? "—"}</span> },
];

export default function CasesTable({
  rows, count, isLoading, ordering, onSort, offset, limit, onOffset, onOpen,
  selected, onToggle, onToggleAll, canTriage, onBulkAck, bulkPending,
}: {
  rows: EventRow[];
  count: number;
  isLoading: boolean;
  ordering: string;
  onSort: (field: string) => void;
  offset: number;
  limit: number;
  onOffset: (n: number) => void;
  onOpen: (id: number) => void;
  selected: Set<number>;
  onToggle: (id: number) => void;
  onToggleAll: () => void;
  canTriage: boolean;
  onBulkAck: () => void;
  bulkPending: boolean;
}) {
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const page = Math.floor(offset / limit) + 1;
  const pages = Math.max(1, Math.ceil(count / limit));

  return (
    <div className="space-y-3">
      {canTriage && selected.size > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-aurora-cyan/30 bg-aurora-cyan/5 px-3 py-2 text-sm">
          <span className="text-aurora-cyan">{selected.size} selected</span>
          <Button variant="ghost" onClick={onBulkAck} disabled={bulkPending}>
            {bulkPending ? "Acknowledging…" : "Acknowledge"}
          </Button>
        </div>
      )}

      <GlassCard className="p-0">
        {isLoading && rows.length === 0 ? (
          <div className="p-6"><Spinner label="Loading cases…" /></div>
        ) : rows.length === 0 ? (
          <div className="p-6"><EmptyState>No cases match these filters.</EmptyState></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-xs">
              <thead>
                <tr className="border-b border-white/10 text-left text-slatey">
                  {canTriage && (
                    <th className="w-8 px-3 py-2">
                      <input type="checkbox" checked={allChecked} onChange={onToggleAll} />
                    </th>
                  )}
                  {COLUMNS.map((c) => (
                    <th key={c.key} className="px-3 py-2 font-medium">
                      <button
                        disabled={!c.sort}
                        onClick={() => c.sort && onSort(c.sort)}
                        className={`flex items-center gap-1 uppercase tracking-wider ${c.sort ? "hover:text-ink" : ""}`}
                      >
                        {c.label}
                        {ordering === c.sort && <ChevronUp size={11} />}
                        {ordering === `-${c.sort}` && <ChevronDown size={11} />}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => onOpen(r.id)}
                    className="cursor-pointer border-b border-white/5 hover:bg-white/[0.03]"
                  >
                    {canTriage && (
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(r.id)} onChange={() => onToggle(r.id)} />
                      </td>
                    )}
                    {COLUMNS.map((c) => (
                      <td key={c.key} className="px-3 py-2 align-top">{c.render(r)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-white/10 px-3 py-2 text-xs text-slatey">
          <span>{count.toLocaleString()} cases{selected.size ? ` · ${selected.size} selected` : ""}</span>
          <div className="flex items-center gap-2">
            <button disabled={offset === 0} onClick={() => onOffset(Math.max(0, offset - limit))} className="hover:text-ink disabled:opacity-30"><ChevronLeft size={16} /></button>
            <span className="mono">{page} / {pages}</span>
            <button disabled={offset + limit >= count} onClick={() => onOffset(offset + limit)} className="hover:text-ink disabled:opacity-30"><ChevronRight size={16} /></button>
          </div>
        </div>
      </GlassCard>
    </div>
  );
}
